import { onKeyDownToggleMark } from '@udecode/plate-common';
import type { InjectComponentProps, PlateEditor } from '@udecode/plate-core';
import {
  createInsertDataPlugin,
  createPluginFactory,
  pipeInjectProps,
} from '@udecode/plate-core';
import classNames from 'classnames';
import { Tooltip } from 'components/atoms/Tooltip';
import type { FC } from 'react';
import { useSelected } from 'slate-react';
import { convertLineBreaksToFragment } from '../EmailClient/utils';

export const ELEMENT_PARAGRAPH = 'p';
export const MARK_BOLD = 'bold';
export const MARK_ITALIC = 'italic';
export const MARK_UNDERLINE = 'underline';
export const ELEMENT_VARIABLE = 'variable';
export const PASTE_PLUGIN = 'paste';

const createBoldStylePlugin = createPluginFactory({
  key: MARK_BOLD,
  isLeaf: true,
  inject: {
    props: {
      transformStyle: (options) => ({ ...options.style, fontWeight: 'bold' }),
    },
  },
  handlers: {
    onKeyDown: onKeyDownToggleMark,
  },
  options: {
    hotkey: 'mod+b',
  },
  deserializeHtml: {
    rules: [
      {
        validStyle: {
          fontWeight: 'bold',
        },
      },
    ],
  },
});

const createItalicStylePlugin = createPluginFactory({
  key: MARK_ITALIC,
  isLeaf: true,
  inject: {
    props: {
      transformStyle: (options) => ({ ...options.style, fontStyle: 'italic' }),
    },
  },
  handlers: {
    onKeyDown: onKeyDownToggleMark,
  },
  options: {
    hotkey: 'mod+i',
  },
  deserializeHtml: {
    rules: [
      {
        validStyle: {
          fontStyle: 'italic',
        },
      },
    ],
  },
});

const createUnderlineStylePlugin = createPluginFactory({
  key: MARK_UNDERLINE,
  isLeaf: true,
  inject: {
    props: {
      transformStyle: (options) => ({
        ...options.style,
        textDecoration: 'underline',
      }),
    },
  },
  handlers: {
    onKeyDown: onKeyDownToggleMark,
  },
  options: {
    hotkey: 'mod+u',
  },
  deserializeHtml: {
    rules: [
      {
        validStyle: {
          textDecoration: 'underline',
        },
      },
    ],
  },
});

const Variable: FC<InjectComponentProps> = (props) => {
  const stylableNode = props.element.children[0]; // { text: "", bold: true };
  const { style } = pipeInjectProps(props.editor, {
    text: stylableNode,
    leaf: stylableNode,
    children: stylableNode.text,
    editor: props.editor,
  });
  const selected = useSelected();
  return (
    <Tooltip content={props.element.tooltip as string | null}>
      <span
        {...props.attributes}
        style={style}
        data-slate-value={props.element.value}
        className={classNames(
          'bg-taxes-100 px-1 rounded inline-block leading-tight',
          {
            'ring-1 ring-taxes-700': selected,
          },
        )}
      >
        {props.children}
        {props.element.value}
      </span>
    </Tooltip>
  );
};

export const createVariablePlugin = createPluginFactory({
  key: ELEMENT_VARIABLE,
  isElement: true,
  isInline: true,
  isVoid: true,
  withOverrides: (editor: PlateEditor) => {
    const { markableVoid } = editor;
    editor.markableVoid = (element) => {
      return element.type === ELEMENT_VARIABLE || markableVoid(element);
    };
    return editor;
  },
  component: (props) => <Variable {...props} />,
  deserializeHtml: {
    getNode: (element) => ({
      type: ELEMENT_VARIABLE,
      value: element.dataset.value,
      variable: element.dataset.variable,
      tooltip: element.dataset.tooltip,
      children: [{ text: '' }],
    }),
    rules: [{ validAttribute: 'data-variable' }],
  },
});

const createParagraphPlugin = createPluginFactory({
  key: ELEMENT_PARAGRAPH,
  isElement: true,
  component: (props) => (
    <p {...props.attributes} style={props.style}>
      {props.children}
    </p>
  ),
  deserializeHtml: {
    rules: [
      {
        validNodeName: ['P', 'DIV'],
      },
    ],
  },
});

const createPastePlugin = createPluginFactory({
  key: PASTE_PLUGIN,
  editor: {
    insertData: {
      format: 'text/plain',
      getFragment: (options) => {
        const text = options.data;
        return convertLineBreaksToFragment(text);
      },
    },
  },
});

const plugins = [
  createParagraphPlugin(),
  createVariablePlugin(),
  createBoldStylePlugin(),
  createItalicStylePlugin(),
  createUnderlineStylePlugin(),
  createInsertDataPlugin(),
  createPastePlugin(),
];

export default plugins;
